import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Mail, Phone, MapPin, ArrowUpRight, Send, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP_URL = "https://wa.me/447961967908?text=Hello%20ACPZ%20Consultancy%2C%20I%27d%20like%20to%20discuss%20your%20metals%20trading%20services.";

const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error("submit failed");
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Couldn't send — please email info@acpzconsultancy.com directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0D12] border border-[#283244] rounded-sm px-4 py-3.5 text-sm text-[#F4F6F8] placeholder:text-[#64748B] focus:border-[#E06D28] focus:outline-none transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-40 overflow-hidden">
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-[760px] max-h-[760px] rounded-full heat-pulse -z-10"
        style={{ background: "radial-gradient(circle, rgba(224,109,40,0.13) 0%, transparent 62%)" }}
      />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono-tech text-xs text-[#64748B]">/05</span>
            <span className="h-px w-12 bg-[#E06D28]/60" />
            <span className="overline-label">Engage ACPZ</span>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <h2 className="font-display font-black tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-7xl max-w-4xl" data-testid="contact-headline">
            Let's move <span className="copper-text-gradient">metal</span> — and protect the margin on it.
          </h2>
        </Reveal>

        <Reveal delay={0.16}>
          <p className="mt-7 text-base sm:text-lg text-[#94A3B8] max-w-2xl leading-relaxed">
            For inquiries on physical base metal sourcing, scrap valuation, risk management structures,
            or supply chain consulting — reach out directly.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
          <Reveal delay={0.2}>
            <a
              href="mailto:info@acpzconsultancy.com"
              data-testid="contact-email-card"
              className="card-industrial p-8 flex items-center justify-between gap-4 group block"
            >
              <div className="flex items-center gap-5">
                <span className="w-12 h-12 rounded-sm bg-[#E06D28]/10 border border-[#E06D28]/30 flex items-center justify-center text-[#F07D38] transition-transform duration-300 group-hover:scale-110">
                  <Mail size={20} />
                </span>
                <div>
                  <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#64748B]">Email</div>
                  <div className="font-display font-bold text-sm text-[#F4F6F8] mt-1 group-hover:text-[#F07D38] transition-colors duration-300 whitespace-nowrap">
                    info@acpzconsultancy.com
                  </div>
                </div>
              </div>
              <ArrowUpRight size={20} className="text-[#64748B] group-hover:text-[#E06D28] transition-colors duration-300 shrink-0" />
            </a>
          </Reveal>

          <Reveal delay={0.28}>
            <a
              href="tel:+447961967908"
              data-testid="contact-phone-card"
              className="card-industrial p-8 flex items-center justify-between gap-4 group block"
            >
              <div className="flex items-center gap-5">
                <span className="w-12 h-12 rounded-sm bg-[#E06D28]/10 border border-[#E06D28]/30 flex items-center justify-center text-[#F07D38] transition-transform duration-300 group-hover:scale-110">
                  <Phone size={20} />
                </span>
                <div>
                  <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#64748B]">Direct Line</div>
                  <div className="font-display font-bold text-lg text-[#F4F6F8] mt-1 group-hover:text-[#F07D38] transition-colors duration-300">
                    +44 7961 967908
                  </div>
                </div>
              </div>
              <ArrowUpRight size={20} className="text-[#64748B] group-hover:text-[#E06D28] transition-colors duration-300 shrink-0" />
            </a>
          </Reveal>

          <Reveal delay={0.34}>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="card-industrial p-8 flex items-center justify-between gap-4 group block"
            >
              <div className="flex items-center gap-5">
                <span className="w-12 h-12 rounded-sm bg-[#25D366]/10 border border-[#25D366]/40 flex items-center justify-center text-[#25D366] transition-transform duration-300 group-hover:scale-110">
                  <WhatsAppIcon size={22} />
                </span>
                <div>
                  <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#64748B]">WhatsApp</div>
                  <div className="font-display font-bold text-lg text-[#F4F6F8] mt-1 group-hover:text-[#25D366] transition-colors duration-300">
                    Chat with the desk
                  </div>
                </div>
              </div>
              <ArrowUpRight size={20} className="text-[#64748B] group-hover:text-[#25D366] transition-colors duration-300 shrink-0" />
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.34}>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#64748B]" data-testid="contact-meta">
            <span className="flex items-center gap-2" data-testid="contact-address"><MapPin size={15} className="text-[#E06D28]" /> 51 Hurrell Drive, Harrow HA2 6DY, UK</span>
            <a href="tel:+442084737419" className="flex items-center gap-2 hover:text-[#F07D38] transition-colors" data-testid="contact-landline"><Phone size={15} className="text-[#E06D28]" /> Landline: +44 20 8473 7419</a>
            <span className="font-mono-tech text-xs uppercase tracking-[0.2em]">Physical Metals · Derivatives Risk · Global Logistics</span>
          </div>
        </Reveal>

        <Reveal delay={0.4}>
          <form data-testid="enquiry-form" onSubmit={submit} className="mt-14 max-w-4xl card-industrial p-7 sm:p-10">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#F07D38] mb-2">Send an Enquiry</div>
            <p className="text-sm text-[#94A3B8] mb-7">Straight to our trading desk — stored securely and emailed to us instantly.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input
                data-testid="enquiry-name-input"
                className={inputCls}
                placeholder="Your name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                maxLength={120}
              />
              <input
                data-testid="enquiry-email-input"
                className={inputCls}
                type="email"
                placeholder="Email address *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <input
                data-testid="enquiry-company-input"
                className={inputCls}
                placeholder="Company (optional)"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                maxLength={160}
              />
            </div>
            <textarea
              data-testid="enquiry-message-input"
              className={`${inputCls} mt-4 min-h-[130px] resize-y`}
              placeholder="Tell us about your material, volumes, incoterms or hedging requirement *"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              minLength={5}
              maxLength={4000}
            />
            <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
              <p className="text-xs text-[#64748B]">We reply within one business day.</p>
              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-[#E06D28] hover:bg-[#F07D38] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-8 py-3.5 rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(224,109,40,0.45)]"
              >
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
